import { accentFor, color, font, radius } from '../../theme/tokens.js';
import { Disclosure } from '../Disclosure/Disclosure.js';

/**
 * A region cell (FR-053, FR-062, SC-003, Constitution Principle I).
 *
 * **The region code is the visible text; the full name is the disclosure.** That is a
 * blueprint rule, not a space-saving choice — `blueprint codebase/CLAUDE.md` states it as a
 * binding table convention, and it holds in every table in the product.
 *
 * The full name is reachable three ways, all carrying the same content (FR-062): by hover, by
 * keyboard focus, and as the cell's accessible name. SC-003 measures this at 100% of region
 * cells with 0% reachable by hover alone.
 */

export interface RegionCellProps {
  regionCode: string;
  regionName: string;
  isSpecial?: boolean;
  onClick?: () => void;
}

export function RegionCell({ regionCode, regionName, isSpecial, onClick }: RegionCellProps) {
  const accent = accentFor(regionCode);
  const fullName = isSpecial ? `${regionName} (Special)` : regionName;

  return (
    <div style={{ padding: '8px 4px' }}>
      <Disclosure
        // Announced as "R3, Midwest" — the code alone would tell a screen-reader user nothing.
        accessibleName={`${regionCode}, ${fullName}`}
        panel={
          <span>
            <strong>{regionCode}</strong>
            <span style={{ display: 'block' }}>{fullName}</span>
          </span>
        }
        onClick={onClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
      >
        <span
          className="mono"
          style={{
            fontFamily: font.mono,
            fontSize: '10px',
            fontWeight: 700,
            color: color.onAccent,
            background: accent,
            padding: '2px 5px',
            borderRadius: radius.sm,
          }}
        >
          {regionCode}
        </span>
      </Disclosure>
    </div>
  );
}
